import type {
  Appearance,
  CssFontSource,
  Stripe,
  StripeElements,
} from '@stripe/stripe-js';

export const stripeHelper = (
  checkoutIdentifier: 'credits' | 'subscription' | string
)  => {
  function getStripeAppearanceObject(): {
    appearance: Appearance;
    fonts: CssFontSource[];
  }{
    return {
      appearance: {
        theme: 'stripe',
        variables: {
          colorBackground: '#292929',
          colorTextPlaceholder: '#e8dbcd',
          fontFamily: 'Outfit',
        },
        rules: {
          '.Input': {
            borderColor: '#e8dbcd',
            color: '#e8dbcd',
            opacity: '0.8',
          },
          '.Label': {
            color: '#e8dbcd',
          },

          '.TermsText': {
            color: '#e8dbcd',
          },
          '.Tab': {
            color: '#e8dbcd',
            borderColor: '#e8dbcd',
          },
        },
      },
      fonts: [
        {
          cssSrc:
            'https://fonts.googleapis.com/css2?family=Outfit&display=swap',
        },
      ],
    };
  }
  async function retrieveCustomerData() {
    const { customerData } = await $fetch<{
      customerData: {
        address: {
          line1: string;
          line2?: string | null;
          city: string;
          state: string;
          postal_code: string;
          country: string;
        };
        name: string;
        phone: string;
        paymentMethod: string;
        companyName: string;
      };
    }>('/api/stripe/customer', {
      method: 'get',
    });
    return customerData;
  }
  async function saveAddressData(
    address?: {
      line1: string;
      line2?: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    },
    name?: string,
    phone?: string,
    taxType?: string,
    taxId?: string,
    companyName?: string
  ) {
    const { stripeCustomerId } = await $fetch<{ stripeCustomerId: string }>(
      '/api/stripe/customer',
      {
        body: {
          address,
          name,
          phone,
          taxType,
          taxId,
          companyName,
        },
        method: 'POST',
        retry: false,
      }
    );

    return stripeCustomerId;
  }

  async function payByInvoice(priceId: string) {
    const { invoiceUrl } = await $fetch<{ invoiceUrl: string }>(
      `/api/stripe/${checkoutIdentifier}/invoice`,
      {
        body: {
          priceId,
        },
        method: 'POST',
      }
    );
    return invoiceUrl;
  }

  async function payByCard(priceId: string, customerId?: string) {
    const { clientSecret, amountDue } = await $fetch<{
      amountDue: number;

      clientSecret: string;
    }>(`/api/stripe/${checkoutIdentifier}/card`, {
      body: { priceId, customerId },
      method: 'POST',
      retry: false,
    });
    return { clientSecret, amountDue };
  }

  async function getVatId() {
    return await $fetch<string | null>(`/api/stripe/vat`, {
      method: 'GET',
      retry: false,
    });
  }

  async function confirmPaymentWithCard(
    stripe: Stripe,
    elements: StripeElements | undefined,
    retrievedSecret: string
  ) {
    if (!stripe || !elements) {
      return null;
    }

    const submit = await elements.submit();

    if (submit.error) {
      console.error(submit.error);
      return false;
    }

    const baseUrl = useRuntimeConfig().public.baseLink;

    // redirects only if payment method is paypal
    const paymentIntent = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      clientSecret: retrievedSecret,
      confirmParams: {
        return_url: `${baseUrl}/api/stripe/${checkoutIdentifier}/redirect`,
      },
    });

    const retrievedPaymentIntent = await stripe.retrievePaymentIntent(
      retrievedSecret
    );

    if (paymentIntent.error) {
      return false;
    }

    if (retrievedPaymentIntent.paymentIntent?.status === 'succeeded') {
      const { success } = await $fetch<{ success: boolean }>(
        `/api/stripe/${checkoutIdentifier}/payment-success`,
        {
          query: {
            paymentIntentId: paymentIntent.paymentIntent?.id,
          },
          redirect: 'manual',
        }
      );

      return success;
    }
    return false;
  }

  async function cancelOpenInvoices() {
    await $fetch('/api/stripe/invoices-cancel', {
      method: 'POST',
    });
  }

  return {
    cancelOpenInvoices,
    confirmPaymentWithCard,
    getStripeAppearanceObject,
    getVatId,
    payByCard,
    payByInvoice,
    retrieveCustomerData,
    saveAddressData,
  };
};
